import { Box, IconButton, Menu, MenuItem, Switch, Tooltip } from "@mui/material";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";
import { AuthContext } from "../../../context/AuthContext";
import { useV2Theme } from "../../hooks/useV2Theme";
import {
  RAIL_WIDTH_COLLAPSED,
  RAIL_WIDTH_EXPANDED,
} from "../../theme/v2Tokens";
import { NAV_ITEMS, RailNavItemDef } from "./railNavItems";
import RailNavItem from "./RailNavItem";
import logo from "../../../images/LogoMenuBar.png";
import { UiVersionToggleMenuItem } from "../UiVersionToggle/UiVersionToggleMenuItem";

interface RailNavProps {
  expanded: boolean;
  onToggle: () => void;
  onArchieToggle: () => void;
}

function getUserInitials(username: string): string {
  const parts = username.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return username.slice(0, 2).toUpperCase();
}

export default function RailNav({ expanded, onToggle, onArchieToggle }: RailNavProps) { // NOSONAR
  const { user, logout } = useContext(AuthContext);
  const { mode, toggleMode } = useV2Theme();
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const { t } = useTranslation(["common", "menuBar", "archie"]);


  const filteredItems = NAV_ITEMS.filter((item) => {
    if (item.featureFlag && !user?.hasFeatureFlag(item.featureFlag)) return false;
    if (!item.resource || !item.requiredPermission) return true;
    return user?.hasPermission(`${item.resource}:${item.requiredPermission}`) ?? false;
  });

  // Group items by section for rendering
  const groupedSections = useMemo(() => {
    const sections: { label: string | undefined; items: RailNavItemDef[] }[] = [];
    let currentLabel: string | undefined = undefined;
    let currentGroup: RailNavItemDef[] = [];

    for (const item of filteredItems) {
      if (item.section !== currentLabel) {
        if (currentGroup.length > 0) {
          sections.push({ label: currentLabel, items: currentGroup });
        }
        currentLabel = item.section;
        currentGroup = [item];
      } else {
        currentGroup.push(item);
      }
    }
    if (currentGroup.length > 0) {
      sections.push({ label: currentLabel, items: currentGroup });
    }
    return sections;
  }, [filteredItems]);

  const railContentRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip animation on initial mount
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const el = railContentRef.current;
    if (!el) return;
    el.animate(
      [
        { transform: expanded ? "translateX(-100%)" : "translateX(100%)", opacity: 0 },
        { transform: "translateX(0)", opacity: 1 },
      ],
      { duration: 200, easing: "ease-out" },
    );
  }, [expanded]);

  const railWidth = expanded ? RAIL_WIDTH_EXPANDED : RAIL_WIDTH_COLLAPSED;
  const initials = user?.username ? getUserInitials(user.username) : "??";
  const hasArchie = user?.hasFeatureFlag("Archie") ?? false;

  return (
    <Box
      component="nav"
      sx={{
        width: railWidth,
        minWidth: railWidth,
        height: "100vh",
        overflow: "hidden",
        flexShrink: 0,
        backgroundColor: "var(--bg-surface)",
        borderRight: "1px solid var(--border-subtle)",
      }}
    >
      <Box
        ref={railContentRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
      {/* Logo */}
      <Box
        sx={{
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: expanded ? "flex-start" : "center",
          px: expanded ? 1.5 : 0,
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Continuity Replay"
          sx={{
            width: 34,
            height: 34,
            borderRadius: "8px",
            flexShrink: 0,
            objectFit: "contain",
          }}
        />
        {expanded && (
          <Box
            component="span"
            sx={{
              ml: 1.25,
              fontFamily: "'DM Sans', -apple-system, sans-serif",
              fontWeight: 500,
              fontSize: "1rem",
              color: "var(--text-primary)",
              letterSpacing: "0",
              whiteSpace: "nowrap",
            }}
          >
            Continuity Replay
          </Box>
        )}
      </Box>

      {/* Nav Items */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.25,
          mt: 0.5,
          px: 0,
          overflowY: "auto",
          overflowX: "hidden",
          flexShrink: 0,
        }}
      >
        {groupedSections.map((section) => (
          <Box key={section.label ?? "__top"} sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            {/* Section divider + label */}
            {section.label && (
              <>
                {/* Thin divider */}
                <Box
                  sx={{
                    height: "1px",
                    backgroundColor: "var(--border-subtle)",
                    mx: expanded ? 1.5 : 1,
                    my: 0.75,
                  }}
                />
                {/* Section label (only when expanded) */}
                {expanded && (
                  <Box
                    sx={{
                      fontSize: "0.625rem",
                      fontWeight: 700,
                      color: "var(--text-muted)",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      px: 2,
                      py: 0.25,
                      userSelect: "none",
                    }}
                  >
                    {section.label}
                  </Box>
                )}
              </>
            )}
            {section.items.map((item) => (
              <RailNavItem key={item.id} item={item} expanded={expanded} />
            ))}
          </Box>
        ))}
      </Box>

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Bottom controls */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
          pb: 1.5,
          px: 0.5,
        }}
      >
        {/* Archie button */}
        {hasArchie && (
          <Tooltip title={t("archie:archieAi")} placement="right" arrow>
            <IconButton
              onClick={onArchieToggle}
              size="small"
              sx={{
                width: expanded ? "calc(100% - 8px)" : 40,
                height: 40,
                borderRadius: "8px",
                background: "var(--archie-gradient)",
                color: "#fff",
                justifyContent: expanded ? "flex-start" : "center",
                px: expanded ? 1.5 : 0,
                gap: 1,
                "&:hover": {
                  opacity: 0.9,
                },
              }}
              aria-label={t("archie:archieAi")}
            >
              <Box
                component="svg"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.75}
                strokeLinecap="round"
                strokeLinejoin="round"
                sx={{ width: 18, height: 18, flexShrink: 0 }}
              >
                <path d="M12 2a4 4 0 0 0-4 4v2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-2V6a4 4 0 0 0-4-4z" /><circle cx="9" cy="13" r="1" /><circle cx="15" cy="13" r="1" />
              </Box>
              {expanded && (
                <Box component="span" sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
                  {t("archie:archie")}
                </Box>
              )}
            </IconButton>
          </Tooltip>
        )}

        {/* Theme toggle */}
        <Tooltip title={expanded ? "" : t("common:switchToTheme", { mode: mode === "dark" ? t("common:lightMode") : t("common:darkMode") })} placement="right" arrow>
          <Box
            component="button"
            onClick={toggleMode}
            aria-label={t("common:toggleTheme")}
            sx={{
              width: expanded ? "calc(100% - 8px)" : 40,
              height: 40,
              borderRadius: "8px",
              color: "var(--text-secondary)",
              display: "flex",
              alignItems: "center",
              justifyContent: expanded ? "flex-start" : "center",
              px: expanded ? 1.5 : 0,
              gap: 1,
              background: "none",
              border: "none",
              cursor: "pointer",
              fontFamily: "inherit",
              "&:hover": {
                backgroundColor: "var(--bg-hover)",
                color: "var(--text-primary)",
              },
            }}
          >
            <Box
              component="svg"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
              sx={{ width: 18, height: 18, flexShrink: 0 }}
            >
              {mode === "dark" ? (
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              ) : (
                <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42M12 5a7 7 0 1 0 0 14A7 7 0 0 0 12 5z" />
              )}
            </Box>
            {expanded && (
              <Box component="span" sx={{ fontSize: "0.8125rem", fontWeight: 500, whiteSpace: "nowrap" }}>
                {mode === "dark" ? t("common:lightMode") : t("common:darkMode")}
              </Box>
            )}
          </Box>
        </Tooltip>

        {/* User avatar (clickable — opens menu) */}
        <Tooltip title={expanded ? "" : (user?.username ?? "")} placement="right" arrow>
          <Box
            onClick={(e) => setUserMenuAnchor(e.currentTarget)}
            sx={{
              width: expanded ? "calc(100% - 8px)" : 34,
              height: expanded ? 40 : 34,
              borderRadius: expanded ? "8px" : "50%",
              backgroundColor: "var(--bg-raised)",
              border: expanded ? "none" : "1px solid var(--border-mid)",
              display: "flex",
              alignItems: "center",
              justifyContent: expanded ? "flex-start" : "center",
              px: expanded ? 1.5 : 0,
              gap: 1,
              color: "var(--text-secondary)",
              fontSize: "0.6875rem",
              fontWeight: 700,
              letterSpacing: "0.05em",
              cursor: "pointer",
              userSelect: "none",
              overflow: "hidden",
              "&:hover": {
                backgroundColor: "var(--bg-hover)",
              },
            }}
          >
            <Box
              sx={{
                width: expanded ? 26 : "auto",
                height: expanded ? 26 : "auto",
                minWidth: expanded ? 26 : "auto",
                borderRadius: "50%",
                backgroundColor: expanded ? "var(--bg-active)" : "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.6875rem",
                flexShrink: 0,
              }}
            >
              {initials}
            </Box>
            {expanded && (
              <Box
                component="span"
                sx={{
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user?.username ?? ""}
              </Box>
            )}
          </Box>
        </Tooltip>
        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={() => setUserMenuAnchor(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: mode === "dark" ? "#1A2332" : "#FFFFFF",
                border: `1px solid ${mode === "dark" ? "rgba(148,163,184,0.15)" : "rgba(15,23,42,0.12)"}`,
                borderRadius: "8px",
                boxShadow: mode === "dark"
                  ? "0 8px 24px rgba(0,0,0,0.5)"
                  : "0 8px 24px rgba(0,0,0,0.12)",
                minWidth: 180,
                color: mode === "dark" ? "#F1F5F9" : "#0F172A",
              },
            },
          }}
        >
          <MenuItem
            disabled
            sx={{
              fontSize: "0.75rem",
              color: "#64748B !important",
              opacity: "1 !important",
              borderBottom: `1px solid ${mode === "dark" ? "rgba(148,163,184,0.08)" : "rgba(15,23,42,0.06)"}`,
            }}
          >
            {user?.username ?? ""}
          </MenuItem>
          <MenuItem
            onClick={() => {
              const newLang = i18n.language === "fr-CA" ? "en" : "fr-CA";
              i18n.changeLanguage(newLang);
            }}
            sx={{
              fontSize: "0.8125rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            {t("common:language")}
            <Switch
              size="small"
              checked={i18n.language === "fr-CA"}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "var(--accent)",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "var(--accent)",
                },
              }}
            />
          </MenuItem>
          <UiVersionToggleMenuItem
            currentUi="v2"
            onClose={() => setUserMenuAnchor(null)}
            sx={{ fontSize: "0.8125rem" }}
          />
          <MenuItem
            onClick={() => {
              setUserMenuAnchor(null);
              logout();
            }}
            sx={{
              fontSize: "0.8125rem",
              color: "#FB7185",
              "&:hover": {
                backgroundColor: "rgba(251,113,133,0.1)",
              },
            }}
          >
            {t("common:logout")}
          </MenuItem>
        </Menu>

        {/* Collapse/expand toggle */}
        <Tooltip title={expanded ? t("common:collapse") : t("common:expand")} placement="right" arrow>
          <IconButton
            onClick={onToggle}
            size="small"
            aria-label={expanded ? t("common:collapseNavigation") : t("common:expandNavigation")}
            sx={{
              width: 40,
              height: 40,
              borderRadius: "8px",
              color: "var(--text-secondary)",
              "&:hover": {
                backgroundColor: "var(--bg-hover)",
                color: "var(--text-primary)",
              },
            }}
          >
            <Box
              component="svg"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
              sx={{ width: 18, height: 18 }}
            >
              {expanded ? (
                <path d="M15 18l-6-6 6-6" />
              ) : (
                <path d="M9 18l6-6-6-6" />
              )}
            </Box>
          </IconButton>
        </Tooltip>
      </Box>
      </Box>
    </Box>
  );
}
